import { useState, useRef } from 'react';
import axios from 'axios';
import { createWorker } from 'tesseract.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Camera, Upload, ScanLine, X, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const s = reader.result || '';
    const idx = s.indexOf(',');
    resolve(idx >= 0 ? s.substring(idx + 1) : s);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Helper to parse OCR text into passport fields (supports MRZ standard & text tags)
const parsePassportOCRText = (text) => {
  if (!text) return {};
  const lines = text.split('\n').map(l => l.trim().toUpperCase()).filter(Boolean);

  let surname = '';
  let givenNames = '';
  let passportNo = '';
  let nationality = 'ZWE';
  let dob = '';
  let sex = '';
  let expiry = '';

  // Look for MRZ lines
  const mrzLines = lines.filter(l => l.includes('<<') || (l.length >= 28 && (l.includes('<') || l.startsWith('P'))));

  if (mrzLines.length >= 2) {
    const line1 = mrzLines.find(l => l.includes('P<') || l.startsWith('P'));
    const line2 = mrzLines.find(l => l !== line1 && /[A-Z0-9<]{25,}/.test(l));

    if (line1) {
      const cleaned = line1.replace(/^P</, '').replace(/^P/, '');
      const parts = cleaned.split('<<');
      if (parts.length >= 2) {
        surname = parts[0].replace(/</g, ' ').trim();
        givenNames = parts[1].replace(/</g, ' ').trim();
      } else if (parts.length === 1) {
        surname = parts[0].replace(/</g, ' ').trim();
      }
    }

    if (line2) {
      const docMatch = line2.match(/([A-Z0-9]{7,9})/);
      if (docMatch) passportNo = docMatch[1];

      const natMatch = line2.match(/([A-Z]{3})/);
      if (natMatch && ['ZWE', 'ZAF', 'MOZ', 'ZMB', 'BWA'].includes(natMatch[1])) {
        nationality = natMatch[1];
      }

      const dobMatch = line2.match(/(\d{6})/);
      if (dobMatch) {
        const yy = parseInt(dobMatch[1].slice(0, 2), 10);
        const mm = dobMatch[1].slice(2, 4);
        const dd = dobMatch[1].slice(4, 6);
        const year = yy > 30 ? 1900 + yy : 2000 + yy;
        dob = `${year}-${mm}-${dd}`;
      }

      const sexMatch = line2.match(/([MF])/);
      if (sexMatch) sex = sexMatch[1];
    }
  }

  // Regex fallback for regular passport photo fields
  if (!passportNo) {
    const pMatch = text.match(/\b([A-Z]{1,2}\d{6,8})\b/i) || text.match(/(?:PASSPORT|DOC)[:\s#]*([A-Z0-9]{7,9})/i);
    if (pMatch) passportNo = pMatch[1].toUpperCase();
  }

  if (!surname && !givenNames) {
    const nameMatch = text.match(/(?:NAME|SURNAME|GIVEN)[:\s]*([A-Z\s]+)/i);
    if (nameMatch) {
      const parts = nameMatch[1].trim().split(/\s+/);
      surname = parts[0] || '';
      givenNames = parts.slice(1).join(' ') || '';
    }
  }

  const fullName = [givenNames, surname].filter(Boolean).join(' ');

  return {
    full_name: fullName,
    given_names: givenNames,
    surname: surname,
    passport_number: passportNo,
    document_number: passportNo,
    nationality: nationality,
    date_of_birth: dob,
    sex: sex,
    expiry_date: expiry,
    confidence: passportNo || fullName ? 0.85 : 0.60
  };
};

const PassportScanner = ({ open, onClose, onResult }) => {
  const { token } = useAuth();
  const [previewSrc, setPreviewSrc] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('idle');
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);

  const reset = () => {
    setPreviewSrc(null);
    setResult(null);
    setMode('idle');
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
  };

  const handleClose = () => { reset(); onClose(); };

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setStream(s);
      setMode('camera');
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play();
        }
      }, 50);
    } catch (e) {
      toast.error('Camera access denied. Try file upload instead.');
    }
  };

  const captureFromCamera = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    const dataUrl = c.toDataURL('image/jpeg', 0.85);
    setPreviewSrc(dataUrl);
    if (stream) { stream.getTracks().forEach(t => t.stop()); setStream(null); }
    setMode('preview');
    await scanImage(dataUrl);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    const b64 = await readAsBase64(file);
    const dataUrl = `data:${file.type};base64,${b64}`;
    setPreviewSrc(dataUrl);
    setMode('preview');
    await scanImage(dataUrl, b64);
  };

  const scanImage = async (dataUrl, b64) => {
    setScanning(true);
    setResult(null);

    let ocrParsed = {};

    // 1. Run real Tesseract.js OCR directly in browser
    try {
      const worker = await createWorker('eng');
      const ret = await worker.recognize(dataUrl);
      await worker.terminate();
      if (ret?.data?.text) {
        ocrParsed = parsePassportOCRText(ret.data.text);
      }
    } catch (err) {
      console.warn('Client Tesseract OCR failed:', err);
    }

    // 2. Query backend for API/Vision OCR as fallback/enhancement
    try {
      const rawB64 = b64 || dataUrl.substring(dataUrl.indexOf(',') + 1);
      const r = await axios.post(
        `${API_URL}/enterprise/ai/passport-ocr`,
        { image_base64: rawB64 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data) {
        // Merge backend result with client OCR
        const merged = {
          full_name: ocrParsed.full_name || r.data.full_name || '',
          passport_number: ocrParsed.passport_number || r.data.passport_number || '',
          document_number: ocrParsed.passport_number || r.data.passport_number || '',
          nationality: ocrParsed.nationality || r.data.nationality || 'ZWE',
          date_of_birth: ocrParsed.date_of_birth || r.data.date_of_birth || '',
          sex: ocrParsed.sex || r.data.sex || '',
          expiry_date: ocrParsed.expiry_date || r.data.expiry_date || '',
          confidence: ocrParsed.confidence || r.data.confidence || 0.8,
          expiry_risk: r.data.expiry_risk || 'ok'
        };
        setResult(merged);
      } else {
        setResult(ocrParsed);
      }
    } catch (e) {
      if (ocrParsed.full_name || ocrParsed.passport_number) {
        setResult(ocrParsed);
      } else {
        setResult({
          full_name: '',
          passport_number: '',
          nationality: 'ZWE',
          date_of_birth: '',
          sex: '',
          expiry_date: '',
          confidence: 0.5
        });
      }
    } finally {
      setScanning(false);
    }
  };

  const applyResult = () => {
    if (!result) return;
    onResult?.(result);
    toast.success('Passport details applied to form');
    handleClose();
  };

  const expiryBadge = () => {
    const risk = result?.expiry_risk;
    if (!risk) return null;
    const map = {
      ok:       { color: 'bg-emerald-100 text-emerald-700', label: 'Valid' },
      warn:     { color: 'bg-amber-100 text-amber-700', label: 'Expires soon' },
      critical: { color: 'bg-orange-100 text-orange-700', label: 'Expiring < 30d' },
      expired:  { color: 'bg-rose-100 text-rose-700', label: 'EXPIRED' },
    };
    const cfg = map[risk] || map.ok;
    return <Badge className={cfg.color}>{cfg.label}{typeof result.expiry_days_left === 'number' && ` · ${result.expiry_days_left}d`}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="passport-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-indigo-600" />
            Passport Scanner
          </DialogTitle>
          <DialogDescription>
            Scan the passport photo page. AI will auto-fill traveller details and flag expiring visas.
          </DialogDescription>
        </DialogHeader>

        {mode === 'idle' && (
          <div className="grid grid-cols-2 gap-3 py-4">
            <Button onClick={startCamera} data-testid="passport-camera-btn" className="h-24 flex flex-col gap-1">
              <Camera className="h-6 w-6" />
              <span>Use Camera</span>
            </Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="passport-upload-btn" className="h-24 flex flex-col gap-1">
              <Upload className="h-6 w-6" />
              <span>Upload Photo</span>
            </Button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="passport-file-input" />
          </div>
        )}

        {mode === 'camera' && (
          <div className="space-y-2">
            <video ref={videoRef} playsInline muted className="w-full rounded-lg bg-black aspect-video" data-testid="passport-video" />
            <canvas ref={canvasRef} className="hidden" />
            <Button onClick={captureFromCamera} className="w-full" data-testid="passport-capture-btn">
              <Camera className="h-4 w-4 mr-1" /> Capture
            </Button>
          </div>
        )}

        {mode === 'preview' && (
          <div className="space-y-3">
            {previewSrc && <img src={previewSrc} alt="Passport preview" className="w-full rounded-lg border max-h-48 object-contain" />}

            {scanning && (
              <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="passport-scanning">
                <Loader2 className="h-4 w-4 animate-spin" /> Reading passport…
              </div>
            )}

            {!scanning && result && (
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg border" data-testid="passport-result">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-700">Extracted Passport Details (Editable)</p>
                  {typeof result.confidence === 'number' && (
                    <Badge variant="outline" className="text-xs">
                      {(result.confidence * 100).toFixed(0)}% confident
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <label className="text-[11px] font-medium text-slate-500 uppercase">Full Name</label>
                    <input
                      type="text"
                      className="w-full mt-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded text-slate-800 text-sm font-medium focus:ring-1 focus:ring-panashe-blue"
                      value={result.full_name || ''}
                      onChange={(e) => setResult(prev => ({ ...prev, full_name: e.target.value }))}
                      placeholder="e.g. Tafadzwa Moyo"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-500 uppercase">Passport #</label>
                    <input
                      type="text"
                      className="w-full mt-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded text-slate-800 text-sm font-mono font-medium focus:ring-1 focus:ring-panashe-blue"
                      value={result.passport_number || result.document_number || ''}
                      onChange={(e) => setResult(prev => ({ ...prev, passport_number: e.target.value, document_number: e.target.value }))}
                      placeholder="e.g. FN789456"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-500 uppercase">Nationality</label>
                    <input
                      type="text"
                      className="w-full mt-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded text-slate-800 text-sm font-medium focus:ring-1 focus:ring-panashe-blue"
                      value={result.nationality || ''}
                      onChange={(e) => setResult(prev => ({ ...prev, nationality: e.target.value }))}
                      placeholder="e.g. ZWE / Zimbabwean"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-500 uppercase">Date of Birth</label>
                    <input
                      type="date"
                      className="w-full mt-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded text-slate-800 text-sm font-medium focus:ring-1 focus:ring-panashe-blue"
                      value={result.date_of_birth || ''}
                      onChange={(e) => setResult(prev => ({ ...prev, date_of_birth: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-500 uppercase">Sex</label>
                    <select
                      className="w-full mt-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded text-slate-800 text-sm font-medium focus:ring-1 focus:ring-panashe-blue"
                      value={result.sex || ''}
                      onChange={(e) => setResult(prev => ({ ...prev, sex: e.target.value }))}
                    >
                      <option value="">Select</option>
                      <option value="M">Male (M)</option>
                      <option value="F">Female (F)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-500 uppercase flex items-center justify-between">
                      Expiry Date {expiryBadge()}
                    </label>
                    <input
                      type="date"
                      className="w-full mt-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded text-slate-800 text-sm font-medium focus:ring-1 focus:ring-panashe-blue"
                      value={result.expiry_date || ''}
                      onChange={(e) => setResult(prev => ({ ...prev, expiry_date: e.target.value }))}
                    />
                  </div>
                </div>

                {result.expiry_risk === 'expired' && (
                  <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                    <AlertTriangle className="h-4 w-4" /> Passport is EXPIRED — traveller cannot cross the border.
                  </div>
                )}
              </div>
            )}

            <Button variant="ghost" size="sm" onClick={reset} data-testid="passport-retake-btn">
              <X className="h-3 w-3 mr-1" /> Retake
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="passport-cancel-btn">Cancel</Button>
          {result && !scanning && (
            <Button onClick={applyResult} data-testid="passport-apply-btn">
              <CheckCircle className="h-4 w-4 mr-1" /> Use these details
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PassportScanner;
